import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';
import styled from 'styled-components';

const ViewerWrapper = styled.div`
  margin-left: ${(props) => (props.depth - 1) * 12}px;
  padding: 4px 0;
`;

const Label = styled.strong`
  display: inline-block;
  min-width: 120px;
  margin-right: 8px;
`;

const DynamicDataViewer = ({ data, depth = 1, maxDepth = 3 }) => {
  if (
    data === null ||
    data === undefined ||
    data === '' ||
    (typeof data === 'object' &&
      !Array.isArray(data) &&
      Object.keys(data).length === 0) ||
    (Array.isArray(data) && data.length === 0)
  ) {
    return null;
  }

  if (depth > maxDepth) {
    return <span>...</span>;
  }

  if (typeof data !== 'object' || data === null) {
    return <span>{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    // Filter out null/undefined items (broken data rows)
    const validData = data.filter(
      (item) => item !== null && item !== undefined
    );

    if (validData.length === 0) {
      return null;
    }

    const columns = [
      ...new Set(
        validData.flatMap((item) =>
          typeof item === 'object' && item !== null ? Object.keys(item) : []
        )
      ),
    ];

    return (
      <Table bordered size="sm" className="mb-2">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {validData.map((item, index) => (
            <tr key={index}>
              {columns.map((col) => (
                <td key={col}>
                  {typeof item === 'object' && item !== null && col in item ? (
                    <DynamicDataViewer
                      data={item[col]}
                      depth={depth + 1}
                      maxDepth={maxDepth}
                    />
                  ) : (
                    <span>-</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  return (
    <ViewerWrapper depth={depth}>
      {Object.entries(data).map(([key, value]) => {
        if (
          value === null ||
          value === undefined ||
          value === '' ||
          (typeof value === 'object' &&
            !Array.isArray(value) &&
            Object.keys(value).length === 0) ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return null;
        }

        const isComplex = typeof value === 'object' && value !== null;

        return (
          <div key={key} style={{ marginBottom: '6px' }}>
            <Label>{key}</Label>
            {isComplex ? (
              <DynamicDataViewer
                data={value}
                depth={depth + 1}
                maxDepth={maxDepth}
              />
            ) : (
              <span>{String(value)}</span>
            )}
          </div>
        );
      })}
    </ViewerWrapper>
  );
};

DynamicDataViewer.propTypes = {
  data: PropTypes.any,
  depth: PropTypes.number,
  maxDepth: PropTypes.number,
};

export default DynamicDataViewer;
